import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import Dropdown from '@lib/inputs/Dropdown';
import Input from '@lib/inputs/Input';
import PhoneNumberInput from '@lib/inputs/PhoneNumberInput';
import ReactDatepicker from '@lib/inputs/react-datepicker';
import { formatDate } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY, YYYY_MM_DD } from '@utils/dates/dateFormats.utils';

import { GridStyledWrapper, StyledDatePicker } from '../voterRegistration.styled';
import useVoterRegistration from '../useVoterRegistration';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';
import { VALIDATIONS } from '../constants/voterRegistrationConstants';
import { createDateWithoutTimezone } from '@utils/dates/dateMethods';

function AboutYouSection() {
    const intl = useIntl();
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<IVoterRegistrationFormValues>();
    const { VOTER_REGISTRATION_CHOICES } = useVoterRegistration();

    const dateOfBirth = watch('dateOfBirth');
    const date = dateOfBirth ? createDateWithoutTimezone(dateOfBirth, YYYY_MM_DD) : null;

    const handleFieldChange = (name: keyof IVoterRegistrationFormValues, value: string | Date | null) => {
        setValue(name, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    return (
        <>
            <FormattedMessage id="voterRegistration.form.aboutYou.header" tagName="h2" />
            <GridStyledWrapper>
                <div id="dateOfBirth-section">
                    <StyledDatePicker>
                        <ReactDatepicker
                            labelText="voterRegistration.form.dateOfBirth"
                            wrapperClasses={['date-picker']}
                            id="dateOfBirth"
                            minDate={new Date(`${VALIDATIONS.VOTING_INFO_CHANGED_YEAR.min}-01-01`)}
                            maxDate={new Date()}
                            type="text"
                            pattern={MM_SLASH_DD_SLASH_YYYY}
                            disabled={false}
                            value={date}
                            withMask
                            isRequired
                            onDateChange={value =>
                                handleFieldChange('dateOfBirth', value ? formatDate(value, YYYY_MM_DD) : '')
                            }
                            data-testid="datepicker-dateFilter"
                            datePickerClass={'datePicker'}
                            noOfYearsToShow={new Date().getFullYear() - VALIDATIONS.VOTING_INFO_CHANGED_YEAR.min}
                            showYearDropdown
                            onDateError={err => {
                                if (!err) {
                                    handleFieldChange('dateOfBirth', '');
                                }
                            }}
                        />
                    </StyledDatePicker>
                    <VoterRegistrationFormError
                        name="dateOfBirth"
                        hasError={!!errors.dateOfBirth}
                        messageId={errors.dateOfBirth?.message}
                    />
                </div>
                <div id="gender-section">
                    <Dropdown
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.gender' }),
                        }}
                        inputData={{
                            id: 'gender',
                            name: 'gender',
                            type: INPUT_TYPES.DROPDOWN,
                            isInputRequired: false,
                            value: watch('gender'),
                            options: VOTER_REGISTRATION_CHOICES.GENDER,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('gender', val),
                        }}
                    />
                </div>
                <div id="race-section">
                    <Dropdown
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.race.title' }),
                        }}
                        inputData={{
                            id: 'race',
                            name: 'race',
                            type: INPUT_TYPES.DROPDOWN,
                            isInputRequired: false,
                            value: watch('race'),
                            options: VOTER_REGISTRATION_CHOICES.RACE,
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('race', val),
                        }}
                    />
                </div>
                <div className="empty" />
                <div id="primaryPhone-section">
                    <PhoneNumberInput
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.primaryPhone' }),
                        }}
                        inputData={{
                            id: 'primaryPhone',
                            name: 'primaryPhone',
                            isInputRequired: false,
                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                            helperClasses: [],
                            value: watch('primaryPhone'),
                            mask: true,
                        }}
                        inputActions={{
                            handleFieldChange: val => {
                                handleFieldChange('primaryPhone', val);
                            },
                        }}
                    />
                    <VoterRegistrationFormError
                        name="primaryPhone"
                        hasError={!!errors.primaryPhone}
                        messageId={errors.primaryPhone?.message}
                    />
                </div>
                <div id="primaryEmail-section">
                    <Input
                        label={{
                            text: intl.formatMessage({ id: 'voterRegistration.form.primaryEmail' }),
                        }}
                        inputData={{
                            id: 'primaryEmail',
                            name: 'primaryEmail',
                            isInputRequired: false,
                            value: watch('primaryEmail'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleFieldChange('primaryEmail', val),
                        }}
                    />
                    <VoterRegistrationFormError
                        name="primaryEmail"
                        hasError={!!errors.primaryEmail}
                        messageId={errors.primaryEmail?.message}
                    />
                </div>
            </GridStyledWrapper>
        </>
    );
}

export default AboutYouSection;
